import {
  assertPayVoxPullDateRange,
  canImportPayVoxDay,
  cashbookPayVoxPullSchema,
  categoryByKey,
  isCashbookOpeningConfigured,
  salesDayKeyForDate,
  sumExpensesByFundingForDay,
  type CashbookEntryRecord,
  type PayVoxCashbookPullPreview,
  type PayVoxCashbookPullPreviewRow
} from '@finvox/shared/cashbook';
import { roundMoney, sumMoney, toMoney } from '@finvox/shared/money';
import {
  buildPayVoxCashbookRows,
  filterPayVoxSalesByDateRange,
  normalizePayVoxSaleFromFirestore,
  payVoxSalesMirrorPaths,
  type PayVoxSaleDoc
} from '@finvox/connectors/payvox';
import { collection, doc, getDocs, query, runTransaction, serverTimestamp, where } from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';
import {
  assertEntryDateInPeriod,
  assertOnOrAfterOpening,
  assertPeriodOpen,
  bumpSyncVersion,
  cashbookEntriesCol,
  invalidateCashbookContextCache,
  loadCashbookSettings,
  mapCashbookEntry
} from './context';
import { isFirestoreIndexError } from './errors';
import type { CashbookPermissions } from './mutations';

function assertCanImport(permissions: CashbookPermissions): void {
  if (!permissions.import) throw new Error('Insufficient permissions');
}

async function loadPayVoxSalesFromMirror(
  orgId: string,
  startDate: string,
  endDate: string
): Promise<PayVoxSaleDoc[]> {
  const db = getFirestoreDb();

  const normalizeDocs = (docs: Array<{ id: string; data: () => Record<string, unknown> | undefined }>) =>
    docs
      .map((d) =>
        normalizePayVoxSaleFromFirestore(d.id, (d.data() ?? {}) as Record<string, unknown>)
      )
      .filter((s): s is PayVoxSaleDoc => s !== null);

  const mergeById = (lists: PayVoxSaleDoc[][]) => {
    const byId = new Map<string, PayVoxSaleDoc>();
    for (const list of lists) {
      for (const sale of list) byId.set(sale.id, sale);
    }
    return [...byId.values()];
  };

  function collectionFromPath(path: string) {
    const [root, org, sub] = path.split('/');
    return collection(db, root!, org!, sub!);
  }

  async function loadFromPath(path: string): Promise<PayVoxSaleDoc[]> {
    const col = collectionFromPath(path);
    try {
      const snap = await getDocs(
        query(col, where('date', '>=', startDate), where('date', '<=', endDate))
      );
      const sales = normalizeDocs(snap.docs);
      if (sales.length > 0) return sales;
    } catch (e) {
      if (!isFirestoreIndexError(e)) throw e;
    }
    const allSnap = await getDocs(col);
    return filterPayVoxSalesByDateRange(normalizeDocs(allSnap.docs), startDate, endDate);
  }

  const paths = payVoxSalesMirrorPaths(orgId);
  const chunks = await Promise.all(paths.map((path) => loadFromPath(path)));
  return mergeById(chunks);
}

async function loadActiveCashbookEntries(orgId: string): Promise<CashbookEntryRecord[]> {
  const db = getFirestoreDb();
  const snap = await getDocs(
    query(cashbookEntriesCol(db, orgId), where('status', '==', 'active'))
  );
  return snap.docs.map((d) => mapCashbookEntry(d.id, d.data()));
}

function enrichPayVoxPreviewRow(
  row: { date: string; amount: string; memo: string; payvoxSaleId?: string; payvoxDayKey?: string },
  entries: CashbookEntryRecord[]
): PayVoxCashbookPullPreviewRow {
  const decision = canImportPayVoxDay(entries, row.date);
  const exp = sumExpensesByFundingForDay(entries, row.date);
  const payvoxAmt = roundMoney(row.amount);
  const net = roundMoney(toMoney(payvoxAmt).minus(toMoney(exp.total)));

  if (decision.status === 'skip') {
    return {
      ...row,
      amount: payvoxAmt,
      dayStatus: 'skip',
      drawerExpenses: exp.drawer,
      treasuryExpenses: exp.treasury,
      unclassifiedExpenses: exp.unclassified,
      incomeToAdd: '0.00',
      netAfterExpenses: net
    };
  }
  if (decision.status === 'blocked') {
    return {
      ...row,
      amount: payvoxAmt,
      dayStatus: 'blocked',
      blockReason: 'manual_income',
      drawerExpenses: exp.drawer,
      treasuryExpenses: exp.treasury,
      unclassifiedExpenses: exp.unclassified,
      incomeToAdd: '0.00',
      netAfterExpenses: net
    };
  }
  return {
    ...row,
    amount: payvoxAmt,
    dayStatus: 'ok',
    drawerExpenses: exp.drawer,
    treasuryExpenses: exp.treasury,
    unclassifiedExpenses: exp.unclassified,
    incomeToAdd: payvoxAmt,
    netAfterExpenses: net
  };
}

export async function previewPayVoxCashbookPull(
  orgId: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<PayVoxCashbookPullPreview> {
  assertCanImport(permissions);
  const data = cashbookPayVoxPullSchema.parse(input);
  const db = getFirestoreDb();
  await assertPeriodOpen(db, orgId, data.periodId);

  const previewMeta = {
    checkedOrgId: orgId
  };

  try {
    assertPayVoxPullDateRange(data.startDate, data.endDate);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Invalid date range';
    return {
      ok: false,
      errors: [message],
      rows: [],
      skippedCount: 0,
      blockedCount: 0,
      totalAmount: '0.00',
      saleCount: 0,
      ...previewMeta
    };
  }

  const settings = await loadCashbookSettings(db, orgId);
  if (!isCashbookOpeningConfigured(settings)) {
    return {
      ok: false,
      errors: ['Set opening treasury first'],
      rows: [],
      skippedCount: 0,
      blockedCount: 0,
      totalAmount: '0.00',
      saleCount: 0,
      ...previewMeta
    };
  }

  const sales = await loadPayVoxSalesFromMirror(orgId, data.startDate, data.endDate);
  const entries = await loadActiveCashbookEntries(orgId);

  // Build candidate rows without old skip-only dedupe — we classify per day ourselves
  const emptySaleIds = new Set<string>();
  const emptyDayKeys = new Set<string>();
  const { rows: builtRows } = buildPayVoxCashbookRows(
    sales,
    data.mode,
    emptySaleIds,
    emptyDayKeys,
    data.locale
  );

  const errors: string[] = [];
  const enriched: PayVoxCashbookPullPreviewRow[] = [];

  for (const row of builtRows) {
    try {
      assertOnOrAfterOpening(settings, row.date);
      await assertEntryDateInPeriod(db, orgId, data.periodId, row.date);
      if (toMoney(row.amount).lte(0)) continue;
      enriched.push(enrichPayVoxPreviewRow(row, entries));
    } catch (e) {
      errors.push(
        e instanceof Error ? `${row.date}: ${e.message}` : `${row.date}: Invalid date`
      );
    }
  }

  const cat = categoryByKey('sales', settings.categories ?? []);
  if (!cat || cat.type !== 'in') {
    return {
      ok: false,
      errors: ['Sales category is not configured'],
      rows: [],
      skippedCount: 0,
      blockedCount: 0,
      totalAmount: '0.00',
      saleCount: sales.length,
      dataSource: 'finvox',
      ...previewMeta
    };
  }

  const importable = enriched.filter((r) => r.dayStatus === 'ok');
  const skippedCount = enriched.filter((r) => r.dayStatus === 'skip').length;
  const blockedCount = enriched.filter((r) => r.dayStatus === 'blocked').length;
  const totalAmount =
    importable.length === 0 ? '0.00' : sumMoney(importable.map((r) => r.incomeToAdd ?? r.amount));

  if (sales.length === 0) {
    const checked = payVoxSalesMirrorPaths(orgId).join(', ');
    errors.push(`No PayVox sales in FinVox mirror (${checked}) for this date range`);
  } else if (importable.length === 0 && skippedCount > 0 && blockedCount === 0 && errors.length === 0) {
    errors.push('All PayVox sales in this range are already imported');
  } else if (importable.length === 0 && blockedCount > 0 && errors.length === 0) {
    errors.push('No days can be imported — remove manual income first or void conflicting lines');
  } else if (importable.length === 0 && errors.length === 0) {
    errors.push('No importable PayVox sales in this range');
  }

  return {
    ok: importable.length > 0 && errors.length === 0,
    errors,
    rows: enriched,
    importableRows: importable,
    skippedCount,
    blockedCount,
    totalAmount,
    saleCount: sales.length,
    dataSource: 'finvox',
    ...previewMeta
  };
}

export async function commitPayVoxCashbookPull(
  orgId: string,
  uid: string,
  input: unknown,
  permissions: CashbookPermissions
): Promise<{ ok: true; imported: number; skippedCount: number; blockedCount: number }> {
  const preview = await previewPayVoxCashbookPull(orgId, input, permissions);
  const toImport = preview.importableRows ?? preview.rows.filter((r) => r.dayStatus === 'ok');
  if (toImport.length === 0) {
    throw new Error(preview.errors[0] ?? 'PayVox import validation failed');
  }

  const data = cashbookPayVoxPullSchema.parse(input);
  const db = getFirestoreDb();

  // Re-check inside txn window against latest entries
  const latest = await loadActiveCashbookEntries(orgId);
  const finalRows = toImport.filter((row) => canImportPayVoxDay(latest, row.date).status === 'ok');
  if (finalRows.length === 0) {
    throw new Error('No days can be imported — conflicting income appeared');
  }

  await runTransaction(db, async (tx) => {
    for (const row of finalRows) {
      const entryRef = doc(cashbookEntriesCol(db, orgId));
      const dayKey = salesDayKeyForDate(row.date);
      tx.set(entryRef, {
        type: 'in',
        amount: roundMoney(row.amount),
        date: row.date,
        categoryKey: 'sales',
        memo: row.memo,
        periodId: data.periodId,
        status: 'active',
        createdBy: uid,
        createdAt: serverTimestamp(),
        imported: true,
        source: 'payvox',
        salesOrigin: 'payvox',
        salesDayKey: dayKey,
        ...(row.payvoxSaleId ? { payvoxSaleId: row.payvoxSaleId } : {}),
        ...(row.payvoxDayKey ? { payvoxDayKey: row.payvoxDayKey } : {})
      });
    }
    bumpSyncVersion(tx, db, orgId);
  });

  invalidateCashbookContextCache(orgId);
  return {
    ok: true,
    imported: finalRows.length,
    skippedCount: preview.skippedCount,
    blockedCount: preview.blockedCount ?? 0
  };
}
